<template>
  <svg
    class="pie"
    viewBox="-1 -1 2 2"
  >
    <template
      v-for="path in paths"
      :key="path.id"
    >
      <path
        :d="path.data"
        :fill="path.color"
      />
    </template>
  </svg>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  /**
   * Sectors of the pie
   *
   * Each sector contains a `size` (from 0 to 1) and a color
   */
  sectors: { type: Array, default: () => [] },
  /**
   * Offset of the pie
   */
  offset: { type: Number, default: 0 },
})

/**
 * Get the cartesian coordinates of an angle on the pie
 * @param {number} angle - Angle from which to determine cartesian coordinates
 * @returns {[number, number]} Cartesian coordinates of the provided angle on the pie
 */
function getPieCoordinates(angle) {
  // Substract π/2 radians (90°) on the provided angle
  // so that the origin of the pie is at 12 O'Clock
  // instead of 3 O'Clock
  const angleFromOrigin = angle - Math.PI * 0.5
  const x = Math.cos(angleFromOrigin)
  const y = Math.sin(angleFromOrigin)
  return [x, y]
}

function svgMove(x, y) {
  // MOVE x y
  return `M ${x} ${y}`
}

function svgArc(radiusX, radiusY, xAxisRotation, largeArcFlag, sweepFlag, x, y) {
  // ARC rx ry x-axis-rotation large-arc-flag sweep-flag x y
  return `A ${radiusX} ${radiusY} ${xAxisRotation} ${largeArcFlag} ${sweepFlag} ${x} ${y}`
}

function svgLine(x, y) {
  // LINE x y
  return `L ${x} ${y}`
}

/**
 * Compute the path of a single sector of the pie
 * @param {number} startAngle - Angle at which the sector starts
 * @param {number} endAngle - Angle at which the sector ends
 */
function computePath(startAngle, endAngle) {
  const [startX, startY] = getPieCoordinates(startAngle)
  const [endX, endY] = getPieCoordinates(endAngle)

  // Create an array and join it later for code readability
  const pathData = [
    // Move to the starting point of the sector
    svgMove(startX, startY),
  ]

  const diff = endAngle - startAngle
  // If the size of the slice is more than half a circle (180° = π radians),
  if (diff > Math.PI) {
    // Draw 2 smaller arcs instead of a big one, because it seems there is a bug
    // in safari that prevents drawing a full circle with a single arc, even with the
    // largeArc flag. The issue is not present on Chrome/Firefox.
    // Maybe this is due to mathematical reasons ?
    // See https://stackoverflow.com/questions/5737975/circle-drawing-with-svgs-arc-path

    // Calculate the intermediate coordinates, aka the end of the first arc
    const [intermediateX, intermediateY] = getPieCoordinates(startAngle + Math.PI)

    pathData.push(svgArc(
      1,
      1,
      0,
      0,
      1,
      intermediateX,
      intermediateY,
    ))
  }

  pathData.push(svgArc(
    1,
    1,
    0,
    0,
    1,
    endX,
    endY,
  ))

  pathData.push(svgLine(0, 0))

  return pathData.join(' ')
}

// Compute the SVG paths of the pie
const paths = computed(() => {
  // Set the base offset
  let startAngle = Math.PI * props.offset * 2

  // Draw each sector, offsetting it by the previous sectors size
  return props.sectors.map((sector) => {
    // Compute the end angle of the sector
    const endAngle = startAngle + Math.PI * sector.size * 2
    const path = {
      id: `${startAngle}-${endAngle}`,
      data: computePath(startAngle, endAngle),
      color: sector.color,
    }
    // Use the current end angle as start angle for the next sector
    startAngle = endAngle
    return path
  })
})
</script>
